import "./PortfolioTransactionEditor.css";
import React, {useCallback} from "react";
import Form from "react-bootstrap/Form";
import {MODE_CREATE, MODE_EDIT, PORTFOLIO_TRANSACTION_TYPE_BUY, PORTFOLIO_TRANSACTION_TYPE_SELL} from "../../../codes";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Editor from "../../../common/Editor";
import usePortfolioTransactionEditor from "./usePortfolioTransactionEditor";
import InputGroup from "react-bootstrap/InputGroup";
import {DatePicker} from "../../../common/DatePicker";
import useProject from "../../../util/useProject";
import {DeleteModal} from "../../../common/AppModal";
import {AccountsDropdown} from "../../../common/AccountsDropdown";
import {CategoriesDropdown} from "../../../common/CategoriesDropdown";
import {RadioField} from "../../../common/RadioField";

export const PortfolioTransactionEditor = () => {
  const {
    show,
    t,
    mode,
    submit,
    hidePortfolioTransactionEditor,
    editedTransaction,
    updateEditedTransaction,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteTransaction,
    availableAccounts,
    availableCategories,
  } = usePortfolioTransactionEditor();

  const {runWithExpiration} = useProject();

  const typeChoices = [
    {value: PORTFOLIO_TRANSACTION_TYPE_BUY, label: t("transaction_editor_type_buy"), activeClass: "success"},
    {value: PORTFOLIO_TRANSACTION_TYPE_SELL, label: t("transaction_editor_type_sell"), activeClass: "danger"}
  ];

  return <Editor
    show={show}
    title={t(mode === MODE_CREATE ? "transaction_editor_title_add" : "transaction_editor_title_edit")}
    onShow={useCallback(() => document.getElementById("stock-transaction-editor-quantity").focus(), [])}
    onSubmit={runWithExpiration(submit)}
    onCancel={hidePortfolioTransactionEditor}
    onDelete={mode === MODE_EDIT ? runWithExpiration(showDeleteConfirmationModal) : null}
    submitLabel={t("transaction_editor_button_save")}
    cancelLabel={t("transaction_editor_button_cancel")}
  >
    <Form.Group as={Row} className="mb-2 mt-2" controlId="stock-transaction-editor-account">
      <Form.Label column sm={3}>{t("transaction_editor_field_account")}</Form.Label>
      <Col sm={9}>
        <AccountsDropdown
          value={editedTransaction.account}
          onChange={updateEditedTransaction("account")}
          accounts={availableAccounts}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-category">
      <Form.Label column sm={3}>{t("transaction_editor_field_category")}</Form.Label>
      <Col sm={9}>
        <CategoriesDropdown
          categories={availableCategories}
          value={editedTransaction.category}
          onChange={updateEditedTransaction("category")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2">
      <Form.Label column sm={3}>{t("transaction_editor_field_type")}</Form.Label>
      <Col sm={9}>
        <RadioField
          value={editedTransaction.type}
          choices={typeChoices}
          onChange={updateEditedTransaction("type")}
        />
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-symbol">
      <Form.Label column sm={3}>{t("transaction_editor_field_symbol")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedTransaction.symbol}
            onChange={updateEditedTransaction("symbol")}
            isInvalid={editedTransaction.errors.symbol}
            disabled
          />

          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.symbol)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-price">
      <Form.Label column sm={3}>{t("transaction_editor_field_price")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedTransaction.price}
            onChange={updateEditedTransaction("price")}
            isInvalid={editedTransaction.errors.price}
          />

          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.price)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-quantity">
      <Form.Label column sm={3}>{t("transaction_editor_field_quantity")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedTransaction.quantity}
            onChange={updateEditedTransaction("quantity")}
            isInvalid={editedTransaction.errors.quantity}
          />

          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.quantity)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-fees">
      <Form.Label column sm={3}>{t("transaction_editor_field_fees")}</Form.Label>
      <Col sm={9}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            value={editedTransaction.fees}
            onChange={updateEditedTransaction("fees")}
            isInvalid={editedTransaction.errors.fees}
          />

          <Form.Control.Feedback type="invalid">{t(editedTransaction.errors.fees)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-value-date">
      <Form.Label column sm={3}>{t("transaction_editor_field_date")}</Form.Label>
      <Col sm={9}>
        <DatePicker value={editedTransaction.valueDate} onChange={updateEditedTransaction("valueDate")}/>
      </Col>
    </Form.Group>

    <Form.Group as={Row} className="mb-2" controlId="stock-transaction-editor-note">
      <Form.Label column sm={3}>{t("transaction_editor_field_note")}</Form.Label>
      <Col sm={9}>
        <Form.Control type="text" value={editedTransaction.note} onChange={updateEditedTransaction("note")}/>
      </Col>
    </Form.Group>

    <DeleteModal
      show={deleteConfirmationModal}
      title={t("modal_delete_title")}
      cancelLabel={t("modal_delete_button_cancel")}
      submitLabel={t("modal_delete_button_apply")}
      onCancel={hideDeleteConfirmationModal}
      onSubmit={onDeleteTransaction}
    >
      {t("modal_delete_body")}
    </DeleteModal>
  </Editor>;
};

